import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const Icon = ({ d, d2 }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d}/>{d2 && <path d={d2}/>}
  </svg>
);

const TUTOR_LINKS = [
  { label:'Dashboard',    path:'/tutor/dashboard',    d:'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' },
  { label:'Bookings',     path:'/tutor/bookings',     d:'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' },
  { label:'Messages',     path:'/tutor/messages',     d:'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' },
  { label:'Availability', path:'/tutor/availability', d:'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z' },
  { label:'Payouts',      path:'/tutor/payouts',      d:'M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6' },
  { label:'My Profile',   path:'/tutor/profile',      d:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', d2:'M12 3a4 4 0 100 8 4 4 0 000-8' },
  { label:'Settings',     path:'/tutor/settings',     d:'M12 15a3 3 0 100-6 3 3 0 000 6z' },
];

const PARENT_LINKS = [
  { label:'Dashboard',    path:'/parent/dashboard',   d:'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' },
  { label:'My Bookings',  path:'/parent/bookings',    d:'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' },
  { label:'Messages',     path:'/parent/messages',    d:'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' },
  { label:'Wallet',       path:'/parent/wallet',      d:'M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5' },
  { label:'Settings',     path:'/parent/settings',    d:'M12 15a3 3 0 100-6 3 3 0 000 6z' },
];

const ADMIN_LINKS = [
  { label:'Dashboard',      path:'/admin/dashboard',        d:'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' },
  { label:'Tutor Queue',    path:'/admin/tutors/pending',   d:'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', d2:'M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
  { label:'Active Tutors',  path:'/admin/tutors/approved',  d:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', d2:'M12 3a4 4 0 100 8 4 4 0 000-8' },
  { label:'Parents',        path:'/admin/parents',          d:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', d2:'M12 3a4 4 0 100 8 4 4 0 000-8' },
  { label:'Bookings',       path:'/admin/bookings',         d:'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' },
  { label:'Disputes',       path:'/admin/disputes',         d:'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { label:'Payouts',        path:'/admin/payouts',          d:'M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6' },
  { label:'Reports',        path:'/admin/reports',          d:'M18 20V10M12 20V4M6 20v-6' },
  { label:'Concerns',       path:'/admin/concerns',         d:'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z', d2:'M12 9v4M12 17h.01' },
];

export default function Sidebar({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const links = role === 'tutor' ? TUTOR_LINKS : role === 'parent' ? PARENT_LINKS : ADMIN_LINKS;
  const initials = user ? (user.first_name?.[0] || '') + (user.last_name?.[0] || '') : '?';

  useEffect(() => {
    if (role === 'tutor' || role === 'parent') {
      const load = () => api.get('/messages/unread/count').then(r => setUnreadCount(r.data.count || 0)).catch(()=>{});
      load();
      const iv = setInterval(load, 10000);
      return () => clearInterval(iv);
    }
  }, [role]);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <NavLink to="/">
          <div className="sidebar-logo-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
            </svg>
          </div>
          <span>Budget<span className="sidebar-logo-accent">Tutor</span></span>
        </NavLink>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <p className="sidebar-section-label">{role === 'admin' ? 'Admin' : role === 'tutor' ? 'Tutor' : 'Parent'}</p>
          {links.map(link => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
            >
              <Icon d={link.d} d2={link.d2} />
              {link.label}
              {link.label === 'Messages' && unreadCount > 0 && (
                <span style={{
                  marginLeft:'auto', background:'#ef4444', color:'#fff',
                  borderRadius:'50%', width:20, height:20, display:'flex',
                  alignItems:'center', justifyContent:'center',
                  fontSize:'0.7rem', fontWeight:800, flexShrink:0
                }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </NavLink>
          ))}
        </div>
        <div className="sidebar-section">
          <p className="sidebar-section-label">Quick links</p>
          <NavLink to="/" className="sidebar-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            </svg>
            Homepage
          </NavLink>
        </div>
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          <div style={{flex:1,minWidth:0}}>
            <div className="sidebar-user-name" style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {user?.first_name} {user?.last_name}
            </div>
            <div className="sidebar-user-role">{role}</div>
          </div>
          <button onClick={()=>{ logout(); navigate('/'); }} style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,0.4)',padding:4}} title="Sign out">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
